import { useState } from 'react'

// V2-8a: the shared Discord-avatar circle — extracted from
// `PortalHome.jsx`'s own `AvatarCircle` (P6-201), which this file replaces
// with an import rather than a second copy. RECONCILIATION.md item 4:
// "those circles by usernames should be Discord profile pics," at the
// design's own sizes (24/30/36/40px) — CLAUDE.md gotcha 1's *copies* axis
// is exactly the hazard a second, hand-rolled avatar component would be:
// two places deciding independently how a broken image or a missing
// picture reads.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 EVERY REASONING NOTE BELOW IS PortalHome.jsx's OWN, CARRIED FORWARD —
//    NOT RE-DERIVED, BECAUSE NONE OF IT CHANGED
// ═══════════════════════════════════════════════════════════════════════════
//
// `avatarUrl` is null far more often than it is a string (`directory.js`'s
// own docstring names the four reasons: an id the directory has never
// seen, a member who has set no picture, a member who has LEFT — the
// publisher drops a departed member's avatar — and a directory that did
// not load at all). The initial-letter placeholder is what every one of
// those four reads as, and it is a first-class state, not an error state —
// nothing here treats it as degraded.
//
// A broken image (a stale URL — an avatar rots the moment somebody changes
// their picture and the directory has not yet re-published) falls back to
// the SAME letter placeholder, never a broken-image glyph — `PosterSlot`'s
// rule, one component over.
//
// `alt=""` inside an `aria-hidden` circle: the person's name is real text
// and is the very next thing in the document on every caller of this
// component (a name always follows an avatar here, never the reverse).
//
// `referrerPolicy="no-referrer"` closes the one site-specific leak in
// hotlinking Discord's CDN (Jordan ruled hotlink over an R2 cache,
// 2026-09-05 — see `PortalHome.jsx`'s own header for the full ruling and
// its written retirement condition, unchanged by this extraction).
//
// ⚠️ `AVATAR_SIZE` (`utils/member_directory.py`, bot-side) is NOT raised by
// this component or anything that calls it. The 88px profile hero (design
// sizes: 24/30/36/40/88) is V2-2's own slice — RECONCILIATION.md item 4 is
// explicit that raising the fetched size belongs there, once a caller
// actually needs it at that resolution on a 2x screen.
//
// `key={person.avatarUrl ?? 'letter'}` at the CALL SITE (not inside this
// component) is what makes `broken` unable to outlive the URL that set it,
// exactly as `PortalHome.jsx` already did — a value keyed on its own input
// rather than reset by an effect.

/**
 * One member's circle: their Discord avatar, or their name's initial.
 *
 * `person` is `nameFor(directory, userId)`'s own return shape
 * (`{label, left, known, avatarUrl}`, `directory.js`) — this component
 * reads only `avatarUrl` from it, so any caller that already resolved a
 * person for other reasons (a name, the `left` suffix) can pass the exact
 * same object rather than resolving twice.
 *
 * `size` is one of the design's own circle sizes — 24, 30, 36 or 40px
 * (Handoff's own ladder; 44 and 88 are call-site overrides via `className`/
 * `imgClassName`, see `PortalHome.jsx`'s own usage below). `initial` is the
 * single character to show when there is no photo — the caller's job,
 * because "which letter" depends on what the caller already knows (a
 * resolved name, or `directory.js::UNKNOWN_NAME`'s own `·`).
 *
 * `testId` names the `<img>` specifically (never the wrapping circle,
 * which is `aria-hidden` and has no text a test would assert against) —
 * defaulted so a new call site costs nothing, overridable so an existing
 * one (`PortalHome.jsx`'s own `portal-avatar-image`) keeps its pre-existing
 * hook rather than forcing unrelated test churn.
 */
export default function AvatarCircle({
  person,
  initial,
  size = 40,
  className = '',
  imgClassName = '',
  testId = 'avatar-image',
}) {
  const [broken, setBroken] = useState(false)
  const src = broken ? null : (person?.avatarUrl ?? null)
  const circleClass = className || `d2d-avatar d2d-avatar--${size}`
  const imgClass = imgClassName || 'd2d-avatarimg'
  return (
    <span className={circleClass} aria-hidden="true">
      {src ? (
        <img
          className={imgClass}
          src={src}
          alt=""
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
          data-testid={testId}
        />
      ) : (
        initial
      )}
    </span>
  )
}
